import { Database } from '@nozbe/watermelondb';

import Entry from '../../Entry';
import {
    InvoiceModel,
    InvoicePayload,
    ParsedInvoiceProduct,
    PreparedDataOnInvoiceUpdate,
} from '../../../../types/Invoice';
import { EntryModel } from '../../../../types/Entries';
import { Identificator } from '../../../../types/Identificator';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { getUpdatedFieldValue } from '../../../utils';
import InvoiceInvoicingProduct from '../../../models/InvoiceInvoicingProduct';
import { toPreparedDataItems } from './common';

type InvoiceUpdateTotals = {
    subtotalToCheck: string;
    totalToCheck: string;
};

const prepareUpdateInvoice = ({
    invoiceElement,
    payload,
    totals,
}: {
    invoiceElement: InvoiceModel;
    payload: Partial<InvoicePayload>;
    totals: InvoiceUpdateTotals;
}) => {
    const { subtotalToCheck, totalToCheck } = totals;

    return invoiceElement.prepareUpdate((invoice) => {
        invoice.number = payload.number || invoiceElement.number;
        invoice.reference = payload.reference || invoiceElement.reference;
        invoice.applyTaxes =
            typeof payload.applyTaxes == 'boolean'
                ? payload.applyTaxes
                : invoiceElement.applyTaxes;
        invoice.status = payload.status || invoiceElement.status;
        invoice.deepLink = payload.deepLink || invoiceElement.deepLink;
        invoice.publicUrl = payload.publicUrl || invoiceElement.publicUrl;

        if (payload.invoicingSystem) {
            invoice.subTotal = !payload.applyTaxes
                ? subtotalToCheck
                : invoiceElement.subTotal;
            invoice.total = payload.applyTaxes
                ? totalToCheck
                : invoiceElement.totalTax;
        } else {
            invoice.subTotal = payload.subTotal || invoiceElement.subTotal;
            invoice.totalTax = payload.totalTax || invoiceElement.totalTax;
        }

        invoice.total = payload.total || invoiceElement.total;
        invoice.totalDiscount =
            payload.totalDiscount || invoiceElement.totalDiscount;
        invoice.sentToContact =
            payload.sentToContact || invoiceElement.sentToContact;
        invoice.authorisedTime =
            payload.authorisedTime || invoiceElement.authorisedTime;
        invoice.dueDateTime = payload.dueDateTime || invoiceElement.dueDateTime;
        invoice.invoiceDate = payload.invoiceDate || invoiceElement.invoiceDate;
        invoice.sentToContactTime =
            payload.sentToContactTime || invoiceElement.sentToContactTime;
        invoice.paymentDateTime =
            payload.paymentDateTime || invoiceElement.paymentDateTime;
        invoice.attachReports =
            payload.attachReports !== undefined
                ? payload.attachReports
                : invoiceElement.attachReports;
        invoice.contactId = payload.contactId || invoiceElement.contactId;
        invoice.externalId = payload.externalId || invoiceElement.externalId;
        invoice.userId = invoiceElement.userId;
        invoice.organisationId = invoiceElement.organisationId;
        invoice.amountCheck = subtotalToCheck;
        invoice.footerText = getUpdatedFieldValue(
            payload.footerText,
            invoiceElement.footerText,
        );
        invoice.taxRate = payload.taxRate || invoiceElement.taxRate;
        invoice.productsTaxRate =
            payload.productsTaxRate || invoiceElement.productsTaxRate;
    });
};

const prepareInvoiceUpdateEntries = async ({
    invoiceId,
    selectedEntriesIds,
    initialEntries,
    entryService,
}: {
    invoiceId: string;
    selectedEntriesIds: Identificator[];
    initialEntries: EntryModel[];
    entryService: Entry;
}) => {
    const entriesToUpdate: EntryModel[] = [];

    await Promise.all(
        selectedEntriesIds.map(async (invoiceEntry) => {
            const initialEntry = initialEntries.find(
                (entry) => entry.id === invoiceEntry.id,
            );

            if (!initialEntry) {
                const entry = await entryService.getByID(invoiceEntry.id);
                const entryToUpdate = entry.prepareUpdate((entry) => {
                    entry.invoiceId = invoiceId;
                });

                entriesToUpdate.push(entryToUpdate);
            }
        }),
    );

    await Promise.all(
        initialEntries.map(async (initialEntry) => {
            if (
                !selectedEntriesIds.find(
                    (invoiceEntry) => invoiceEntry.id === initialEntry.id,
                )
            ) {
                const entry = await entryService.getByID(initialEntry.id);
                const entryToUpdate = entry.prepareUpdate((entry) => {
                    entry.invoiceId = null;
                });

                entriesToUpdate.push(entryToUpdate);
            }
        }),
    );

    return { entriesToUpdate };
};

const prepareInvoiceUpdateProducts = async ({
    invoiceId,
    selectedProducts,
    initialProducts,
    invoiceElement,
    database,
}: {
    invoiceId: string;
    selectedProducts: ParsedInvoiceProduct[];
    initialProducts: InvoiceInvoicingProduct[] | null;
    invoiceElement: InvoiceModel;
    database: Database;
}) => {
    const productsToAdd: InvoiceInvoicingProduct[] = [];
    const productsToUpdate: InvoiceInvoicingProduct[] = [];
    const productsToDelete: InvoiceInvoicingProduct[] = [];

    const invoiceProductsCollection =
        database.collections.get<InvoiceInvoicingProduct>(
            'invoice_invoicing_products',
        );

    await Promise.all(
        selectedProducts.map(async (product) => {
            const initialProduct = initialProducts?.find(
                (ip) => ip.invoicingProductId === product.invoicingProductId,
            );

            if (!initialProduct) {
                const newInvoiceProduct =
                    invoiceProductsCollection.prepareCreate(
                        (invoiceInvoicingProduct: InvoiceInvoicingProduct) => {
                            invoiceInvoicingProduct.invoiceId = invoiceId;
                            invoiceInvoicingProduct.invoicingProductId =
                                product.invoicingProductId;
                            invoiceInvoicingProduct.quantity = product.quantity;
                            invoiceInvoicingProduct.userId =
                                invoiceElement.userId;
                            invoiceInvoicingProduct.organisationId =
                                invoiceElement.organisationId;
                            invoiceInvoicingProduct.price = product.price;
                            invoiceInvoicingProduct.name = product.name;
                        },
                    );
                productsToAdd.push(newInvoiceProduct);
            } else {
                const productToUpdate = initialProduct.prepareUpdate(
                    (p: InvoiceInvoicingProduct) => {
                        p.quantity = product.quantity;
                    },
                );
                productsToUpdate.push(productToUpdate);
            }
        }),
    );

    if (initialProducts) {
        await Promise.all(
            initialProducts.map(async (initialProduct) => {
                if (
                    !selectedProducts.find(
                        (product) =>
                            product.invoicingProductId ===
                            initialProduct.invoicingProductId,
                    )
                ) {
                    const productToDelete =
                        initialProduct.prepareMarkAsDeleted();
                    productsToDelete.push(productToDelete);
                }
            }),
        );
    }

    return { productsToAdd, productsToUpdate, productsToDelete };
};

const appendPreparedInvoice = (
    preparedData: PreparedDataOnInvoiceUpdate[],
    invoice: InvoiceModel,
): PreparedDataOnInvoiceUpdate[] => {
    return [
        ...preparedData,
        ...toPreparedDataItems([invoice], 'invoices', 'update'),
    ];
};

const appendPreparedInvoiceUpdateEntries = (
    preparedData: PreparedDataOnInvoiceUpdate[],
    entriesData: Awaited<ReturnType<typeof prepareInvoiceUpdateEntries>>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            entriesData.entriesToUpdate,
            'entries',
            'update',
        ),
    ];
};

const appendPreparedInvoiceUpdateProducts = (
    preparedData: PreparedDataOnInvoiceUpdate[],
    productsData: Awaited<ReturnType<typeof prepareInvoiceUpdateProducts>>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            productsData.productsToAdd,
            'invoice_invoicing_products',
            'add',
        ),
        ...toPreparedDataItems(
            productsData.productsToUpdate,
            'invoice_invoicing_products',
            'update',
        ),
        ...toPreparedDataItems(
            productsData.productsToDelete,
            'invoice_invoicing_products',
            'delete',
        ),
    ];
};

const logUpdateDBActions = ({
    invoiceElement,
    entriesData,
    logDBAction,
    tableName,
}: {
    invoiceElement: InvoiceModel;
    entriesData?: Awaited<ReturnType<typeof prepareInvoiceUpdateEntries>>;
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    logDBAction({
        message: 'Update invoice',
        modelName: tableName,
        payload: invoiceElement,
    });

    logDBAction({
        message: 'Update invoice - update entries',
        modelName: tableName,
        payload: entriesData?.entriesToUpdate ?? [],
    });
};

export {
    appendPreparedInvoice,
    appendPreparedInvoiceUpdateEntries,
    appendPreparedInvoiceUpdateProducts,
    logUpdateDBActions,
    prepareInvoiceUpdateEntries,
    prepareInvoiceUpdateProducts,
    prepareUpdateInvoice,
};
